import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiPlusCircle, FiMinusCircle, FiArrowLeft, FiRefreshCcw, FiExternalLink } = FiIcons;

const AdminUserStatement = ({ userProfile, onUpdateCredits, onBack }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [donateAmount, setDonateAmount] = useState(1);
  const [localProfile, setLocalProfile] = useState(userProfile);
  const [paymentsMap, setPaymentsMap] = useState({});

  // Sync with prop if it changes
  useEffect(() => {
    setLocalProfile(userProfile);
  }, [userProfile]);

  const fetchLogs = useCallback(async () => {
    if (!localProfile?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_credit_logs')
        .select('*')
        .eq('user_id', localProfile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLogs(data || []);

      const purchaseLogs = data?.filter(l => l.type === 'purchase') || [];
      if (purchaseLogs.length > 0) {
        const paymentIds = purchaseLogs.map(l => {
          const match = l.description.match(/ID: (pay_[a-z0-9]+)/);
          return match ? match[1] : null;
        }).filter(Boolean);

        if (paymentIds.length > 0) {
          const { data: payments } = await supabase
            .from('app_payments_v2')
            .select('asaas_payment_id, billing_type, payload')
            .in('asaas_payment_id', paymentIds);
          
          const map = {};
          payments?.forEach(p => { map[p.asaas_payment_id] = p; });
          setPaymentsMap(map);
        }
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, [localProfile.id]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleAdjustCredits = async (amount, type) => {
    if (!localProfile?.id) return;
    
    const action = amount > 0 ? 'adicionar' : 'remover';
    const absoluteAmount = Math.abs(amount);
    
    if (!window.confirm(`Tem certeza que deseja ${action} ${absoluteAmount} crédito(s) para este usuário?`)) return;

    const currentCredits = Number(localProfile.credits) || 0;
    const newTotal = Math.max(0, currentCredits + amount);

    try {
      const { error: profileError } = await supabase
        .from('app_profiles')
        .update({ 
          credits: newTotal,
          updated_at: new Date().toISOString()
        })
        .eq('id', localProfile.id);

      if (profileError) throw profileError;

      const { error: logError } = await supabase.from('app_credit_logs').insert({
        user_id: localProfile.id,
        amount: amount,
        type: type,
        description: type === 'donation' ? 'Bônus administrativo' : 'Ajuste administrativo'
      });

      if (logError) throw logError;

      setLocalProfile(prev => ({ ...prev, credits: newTotal }));
      fetchLogs();
      if (onUpdateCredits) onUpdateCredits();
      
      alert(`Sucesso! ${absoluteAmount} crédito(s) ${amount > 0 ? 'adicionados' : 'removidos'}.`);
    } catch (err) {
      console.error('Update error:', err);
      alert('Erro ao processar ajuste: ' + (err.message || 'Erro desconhecido'));
    }
  };

  const renderDescription = (log) => {
    if (log.type === 'purchase') {
      const match = log.description.match(/ID: (pay_[a-z0-9]+)/);
      const paymentId = match ? match[1] : null;
      const payment = paymentsMap[paymentId];
      
      if (payment) {
        const method = payment.billing_type === 'UNDEFINED' ? 'Asaas' : payment.billing_type;
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-bold uppercase tracking-tight">
              Pagamento via {method}
            </span>
            {payment.payload?.invoiceUrl && (
              <a 
                href={payment.payload.invoiceUrl} target="_blank" rel="noopener noreferrer"
                className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
              </a>
            )}
          </div>
        );
      }
      return <span className="text-xs text-slate-500 font-bold uppercase tracking-tight">Pagamento Realizado</span>;
    }
    return <span className="text-xs text-slate-500 font-bold uppercase tracking-tight">{log.description}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={onBack}
          className="w-fit flex items-center gap-2 px-4 py-2 bg-white border border-slate-100 rounded-xl text-slate-500 font-bold text-xs hover:bg-slate-50 transition-all shadow-sm"
        >
          <SafeIcon icon={FiArrowLeft} /> Voltar para Lista
        </button>
        <div className="md:text-right bg-white p-4 rounded-2xl border border-slate-100 shadow-sm md:min-w-[250px]">
          <h3 className="font-black text-slate-900 uppercase italic leading-none truncate">{localProfile.full_name}</h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase mt-2">
            Saldo Atual: <span className="text-indigo-600 text-sm ml-1">{localProfile.credits} Créditos</span>
          </p>
        </div>
      </div>

      <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex flex-col lg:flex-row justify-between items-center gap-6">
          <div>
            <h4 className="font-black text-slate-900 uppercase italic text-sm">Ajuste de Saldo</h4>
            <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 tracking-widest">Controle manual de créditos para este usuário</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 bg-white p-2.5 rounded-[2rem] border border-slate-100 shadow-sm">
            <div className="flex items-center bg-slate-50 px-3 py-2 rounded-xl border border-slate-100">
              <span className="text-[9px] font-black text-slate-400 uppercase mr-2">Qtd:</span>
              <input 
                type="number" min="1" value={donateAmount} 
                onChange={(e) => setDonateAmount(Math.max(1, parseInt(e.target.value) || 0))} 
                className="w-16 bg-transparent border-none rounded-lg text-sm font-black text-center focus:ring-0 outline-none"
              />
            </div>
            <button 
              onClick={() => handleAdjustCredits(donateAmount, 'donation')} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase flex items-center transition-all shadow-lg shadow-indigo-100"
            >
              <SafeIcon icon={FiPlusCircle} className="mr-2" /> Adicionar
            </button>
            <button 
              onClick={() => handleAdjustCredits(-donateAmount, 'removal')} 
              className="bg-slate-900 hover:bg-red-600 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase flex items-center transition-all shadow-lg shadow-slate-200"
            >
              <SafeIcon icon={FiMinusCircle} className="mr-2" /> Remover
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading && logs.length === 0 ? (
            <div className="p-20 text-center">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Carregando transações...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-20 text-center">
              <SafeIcon icon={FiRefreshCcw} className="text-4xl text-slate-100 mx-auto mb-4" />
              <p className="text-slate-300 italic font-bold uppercase text-xs">Nenhuma transação registrada para este usuário.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50 text-slate-400 uppercase text-[10px] font-black tracking-widest">
                <tr className="border-b border-slate-100">
                  <th className="px-8 py-4 text-left">Data e Hora</th>
                  <th className="px-8 py-4 text-left">Tipo</th>
                  <th className="px-8 py-4 text-left">Descrição</th>
                  <th className="px-8 py-4 text-right">Quantidade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-8 py-4">
                      <div className="flex flex-col">
                        <span className="font-black text-slate-700 text-xs">{new Date(log.created_at).toLocaleDateString()}</span>
                        <span className="text-[10px] text-slate-400 font-bold uppercase">{new Date(log.created_at).toLocaleTimeString()}</span>
                      </div>
                    </td>
                    <td className="px-8 py-4">
                      <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-tighter shadow-sm border ${
                        log.amount > 0 
                          ? 'bg-green-50 text-green-600 border-green-100' 
                          : 'bg-red-50 text-red-600 border-red-100'
                      }`}>
                        {log.type === 'purchase' ? 'Compra' : 
                         log.type === 'usage' ? 'Uso' : 
                         log.type === 'donation' ? 'Bônus' : 'Remoção'}
                      </span>
                    </td>
                    <td className="px-8 py-4">
                      {renderDescription(log)}
                    </td>
                    <td className={`px-8 py-4 text-right font-black text-sm ${
                      log.amount > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {log.amount > 0 ? '+' : ''}{log.amount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserStatement;